// src/services/sensorInsightsService.js
// FlowSense — sensor details + history data access — NEW FILE
// ═══════════════════════════════════════════════════════════════════════════
// fetchSensorDetailsBundle(deviceId)
//   GET /sensors/:id/context → { sensor (enriched: latest_reading, online,
//   last_activity_at, ai_predictions), asset, readings (newest 120),
//   open_work_orders, active_alerts }. Falls back to GET /sensors/:id when the
//   context route is missing (older backend), and returns registered:false when
//   the device isn't in the register at all.
// fetchSensorSeries(deviceId, { range, points })
//   GET /sensors/:id/series — the whole range grouped in MySQL.
// fetchSensorHistoryRaw(deviceId, { start, limit })
//   GET /sensors/:id/history — fallback when /series isn't deployed yet.
// Results are cached briefly (stale-while-revalidate) and concurrent calls for
// the same key share one request.
// ═══════════════════════════════════════════════════════════════════════════
import { authenticatedFetch } from './api';
import * as SensorService from './sensorService';

const cache = new Map();    // key -> { data, ts }
const inflight = new Map(); // key -> Promise

export const isNotFoundError = (e) =>
  !!e && (e.status === 404 || e.statusCode === 404 || e.response?.status === 404 || /\b404\b|not found/i.test(String(e.message || '')));

const enc = (id) => encodeURIComponent(String(id));

async function cached(key, ttlMs, loader, { force = false } = {}) {
  const hit = cache.get(key);
  if (!force && hit && Date.now() - hit.ts < ttlMs) return hit.data;
  if (inflight.has(key)) return inflight.get(key);
  const p = (async () => {
    try {
      const data = await loader();
      cache.set(key, { data, ts: Date.now() });
      if (cache.size > 200) cache.delete(cache.keys().next().value);
      return data;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

/** Last cached value (any age) — lets a drawer paint instantly on reopen. */
export const peekCached = (key) => cache.get(key)?.data ?? null;
export const detailsKey = (deviceId) => `details:${String(deviceId).trim().toUpperCase()}`;
export const seriesKey = (deviceId, range, points) => `series:${String(deviceId).trim().toUpperCase()}:${range}:${points}`;

/**
 * @returns {Promise<{sensor:object|null, asset:object|null, readings:object[], open_work_orders:object[],
 *   active_alerts:object[], kind:string|null, registered:boolean, source:'context'|'sensor'|'none'}>}
 */
export function fetchSensorDetailsBundle(deviceId, { force = false, ttlMs = 30000 } = {}) {
  if (!deviceId) return Promise.resolve(null);
  return cached(detailsKey(deviceId), ttlMs, async () => {
    try {
      const res = await authenticatedFetch(`/sensors/${enc(deviceId)}/context`);
      const d = res?.data || res;
      if (d && d.sensor) {
        return {
          sensor: d.sensor,
          asset: d.asset || null,
          readings: Array.isArray(d.readings) ? d.readings : [],
          open_work_orders: Array.isArray(d.open_work_orders) ? d.open_work_orders : [],
          active_alerts: Array.isArray(d.active_alerts) ? d.active_alerts : [],
          kind: d.kind || null,
          registered: true,
          source: 'context',
        };
      }
    } catch (e) {
      if (!isNotFoundError(e)) throw e;
      // 404: older backend without /context, or the device isn't registered — try the plain record
    }
    const sensor = await SensorService.fetchSensorById(deviceId); // null on 404
    return {
      sensor: sensor || null,
      asset: null,
      readings: [],
      open_work_orders: [],
      active_alerts: [],
      kind: null,
      registered: !!sensor,
      source: sensor ? 'sensor' : 'none',
    };
  }, { force });
}

export function fetchSensorSeries(deviceId, { range = '24h', points = 240, force = false, ttlMs = 30000 } = {}) {
  return cached(seriesKey(deviceId, range, points), ttlMs, async () => {
    const res = await authenticatedFetch(`/sensors/${enc(deviceId)}/series?range=${encodeURIComponent(range)}&points=${points}`);
    return res?.data || res;
  }, { force });
}

export async function fetchSensorHistoryRaw(deviceId, { start, limit = 1000 } = {}) {
  const params = new URLSearchParams();
  if (start) params.append('start', start.toISOString());
  params.append('limit', String(limit));
  const res = await authenticatedFetch(`/sensors/${enc(deviceId)}/history?${params.toString()}`);
  return {
    kind: res?.kind || null,
    rows: Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : Array.isArray(res?.readings) ? res.readings : [],
  };
}

/** Drop cached details / series for a device (e.g. after relinking it). */
export function invalidateSensor(deviceId) {
  const k = String(deviceId).trim().toUpperCase();
  [...cache.keys()].forEach((key) => { if (key.split(':')[1] === k) cache.delete(key); });
}
